/** ノードのアイコン（docs/05_rendering-and-audio.md 9 章）。画面上で一定の大きさの点として描き、常に手前に表示する */
import * as THREE from 'three';
import type { NodeData, NodeKind } from '../../core/world';

export type MarkerState = 'normal' | 'hover' | 'route' | 'dim';

const SHAPE = { circle: 0, triangle: 1, diamond: 2, ring: 3 } as const;

const STYLE: Record<NodeKind, { color: number; shape: number; size: number }> = {
  start: { color: 0x22d3ee, shape: SHAPE.ring, size: 26 },
  checkpoint: { color: 0xfacc15, shape: SHAPE.ring, size: 26 },
  goal: { color: 0xe879f9, shape: SHAPE.ring, size: 28 },
  valley: { color: 0xe2e8f0, shape: SHAPE.circle, size: 11 },
  pass: { color: 0xd6a36b, shape: SHAPE.diamond, size: 15 },
  launchLow: { color: 0x4ade80, shape: SHAPE.triangle, size: 15 },
  launchRidge: { color: 0xfb923c, shape: SHAPE.triangle, size: 17 },
  launchPeak: { color: 0xef4444, shape: SHAPE.triangle, size: 19 },
  temp: { color: 0x94a3b8, shape: SHAPE.circle, size: 11 },
};

export function nodeKindColor(kind: NodeKind): string {
  return `#${STYLE[kind].color.toString(16).padStart(6, '0')}`;
}

const STATE_VALUE: Record<MarkerState, number> = { normal: 0, hover: 1, route: 2, dim: 3 };

export class NodeMarkers {
  readonly object: THREE.Points;
  private readonly state: THREE.BufferAttribute;
  private readonly worldPos: THREE.Vector3[];
  private readonly material: THREE.ShaderMaterial;

  constructor(private readonly nodes: NodeData[]) {
    const n = nodes.length;
    const pos = new Float32Array(n * 3);
    const color = new Float32Array(n * 3);
    const size = new Float32Array(n);
    const shape = new Float32Array(n);
    const c = new THREE.Color();
    this.worldPos = nodes.map((node, i) => {
      const s = STYLE[node.kind];
      pos.set([node.pos.x, node.pos.y + 20, node.pos.z], i * 3);
      c.setHex(s.color);
      color.set([c.r, c.g, c.b], i * 3);
      size[i] = s.size;
      shape[i] = s.shape;
      return new THREE.Vector3(node.pos.x, node.pos.y + 20, node.pos.z);
    });
    this.state = new THREE.BufferAttribute(new Float32Array(n), 1);

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aColor', new THREE.BufferAttribute(color, 3));
    geo.setAttribute('aSize', new THREE.BufferAttribute(size, 1));
    geo.setAttribute('aShape', new THREE.BufferAttribute(shape, 1));
    geo.setAttribute('aState', this.state);

    this.material = new THREE.ShaderMaterial({
      transparent: true,
      depthTest: false,
      depthWrite: false,
      uniforms: { pixelRatio: { value: 1 } },
      vertexShader: /* glsl */ `
        attribute vec3 aColor;
        attribute float aSize;
        attribute float aShape;
        attribute float aState;
        uniform float pixelRatio;
        varying vec3 vColor;
        varying float vShape;
        varying float vState;
        void main() {
          vColor = aColor;
          vShape = aShape;
          vState = aState;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          float s = aSize * (aState > 0.5 && aState < 1.5 ? 1.45 : aState > 1.5 && aState < 2.5 ? 1.2 : 1.0);
          gl_PointSize = s * pixelRatio;
        }`,
      fragmentShader: /* glsl */ `
        varying vec3 vColor;
        varying float vShape;
        varying float vState;
        float sdTriangle(vec2 p, float r) {
          const float k = 1.7320508;
          p.x = abs(p.x) - r;
          p.y = p.y + r / k;
          if (p.x + k * p.y > 0.0) p = vec2(p.x - k * p.y, -k * p.x - p.y) / 2.0;
          p.x -= clamp(p.x, -2.0 * r, 0.0);
          return -length(p) * sign(p.y);
        }
        void main() {
          vec2 p = gl_PointCoord * 2.0 - 1.0;
          p.y = -p.y;
          float d;
          if (vShape < 0.5) d = length(p) - 0.78;
          else if (vShape < 1.5) d = sdTriangle(p + vec2(0.0, 0.18), 0.82);
          else if (vShape < 2.5) d = abs(p.x) + abs(p.y) - 0.86;
          else d = abs(length(p) - 0.6) - 0.26;
          float aa = fwidth(d) * 1.2;
          float alpha = 1.0 - smoothstep(-aa, aa, d);
          if (alpha < 0.01) discard;
          float outline = smoothstep(-0.2 - aa, -0.2 + aa, d);
          vec3 edgeColor = vState > 0.5 && vState < 2.5 ? vec3(1.0) : vec3(0.05, 0.07, 0.1);
          vec3 col = mix(vColor, edgeColor, outline);
          float a = alpha * (vState > 2.5 ? 0.35 : 1.0);
          gl_FragColor = vec4(col, a);
          #include <colorspace_fragment>
        }`,
    });
    this.object = new THREE.Points(geo, this.material);
    this.object.frustumCulled = false;
    this.object.renderOrder = 20;
  }

  setPixelRatio(ratio: number): void {
    (this.material.uniforms.pixelRatio as THREE.IUniform<number>).value = ratio;
  }

  setStates(states: Map<number, MarkerState>): void {
    const arr = this.state.array as Float32Array;
    arr.fill(0);
    for (const [id, s] of states) arr[id] = STATE_VALUE[s];
    this.state.needsUpdate = true;
  }

  /** 画面上の位置 (px) から、いちばん近いノード（半径 radiusPx 以内） */
  pick(
    camera: THREE.Camera,
    px: number,
    py: number,
    width: number,
    height: number,
    radiusPx = 14,
  ): number | null {
    const v = new THREE.Vector3();
    let best: number | null = null;
    let bestD = radiusPx;
    this.worldPos.forEach((wp, i) => {
      v.copy(wp).project(camera);
      if (v.z < -1 || v.z > 1) return;
      const sx = ((v.x + 1) / 2) * width;
      const sy = ((1 - v.y) / 2) * height;
      const size = STYLE[(this.nodes[i] as NodeData).kind].size;
      const d = Math.hypot(sx - px, sy - py) - size * 0.25;
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    return best;
  }
}
